// src/pages/AdminDashboard.tsx
import React, { useEffect, useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChurchIcon from '@mui/icons-material/Church';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import RepeatIcon from '@mui/icons-material/Repeat';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import AlarmIcon from '@mui/icons-material/Alarm';
import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { getEgliseById, getEgliseUsers, getEgliseEvents, removeUserFromEglise } from '../services/egliseService';
import { deleteEvent } from '../services/eventService';
import { notifyEventCreation, createEventReminders } from '../services/notificationService';
import { Eglise, EgliseUser, Event } from '../types/church';
import CreateEventDialog from '../components/CreateEventDialog';
import EditChurchDialog from '../components/EditChurchDialog';
import EditEventDialog from '../components/EditEventDialog';
import CreateEnrichmentDialog from '../components/CreateEnrichmentDialog';
// CreatePostDialog déplacé vers PostsPage
import CreateAlarmDialog from '../components/CreateAlarmDialog';
import EnrichmentsView from '../components/EnrichmentsView';
import PostsView from '../components/PostsView';
import AddMemberDialog from '../components/AddMemberDialog';
import FeedbacksView from '../components/FeedbacksView';
import BulkNotificationDialog from '../components/BulkNotificationDialog';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import TabPanel from '../components/common/TabPanel';

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState, getUserEglises } = useAuth();
  const { success, error: showError } = useNotification();
  const user = authState.user;

  const [church, setChurch] = useState<Eglise | null>(null);
  const [users, setUsers] = useState<EgliseUser[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [adminChurchId, setAdminChurchId] = useState<number | null>(null);

  // Dialogs state
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [openEditChurchDialog, setOpenEditChurchDialog] = useState(false);
  const [openEditEventDialog, setOpenEditEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openEnrichmentDialog, setOpenEnrichmentDialog] = useState(false);
  const [openAlarmDialog, setOpenAlarmDialog] = useState(false);
  const [openAddMemberDialog, setOpenAddMemberDialog] = useState(false);
  const [openBulkNotificationDialog, setOpenBulkNotificationDialog] = useState(false);
  const [selectedEventForAlarm, setSelectedEventForAlarm] = useState<number | null>(null);

  // Date selection for enrichments/posts
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const selectedYear = new Date().getFullYear();
  const [refreshKey, setRefreshKey] = useState(0);

  // Delete confirmation dialogs
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<EgliseUser | null>(null);

  // Loading states for actions
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [removingUser, setRemovingUser] = useState(false);

  // Sorting state for users table
  const [userSortField, setUserSortField] = useState<'name' | 'email' | 'role' | 'last_activity'>('name');
  const [userSortDirection, setUserSortDirection] = useState<'asc' | 'desc'>('asc');

  // Events filtering and sorting state
  const [eventFilterTab, setEventFilterTab] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<'all' | 'recurring' | 'oneTime'>('all');
  const [eventSortDirection, setEventSortDirection] = useState<'asc' | 'desc'>('asc');

  // CSV Export function
  const exportUsersToCSV = () => {
    if (users.length === 0) return;

    const headers = ['Nom', 'Email', 'Téléphone', 'Rôle', 'Admin Église', 'Date Adhésion', 'Dernière Activité'];
    const csvRows = [headers.join(',')];

    users.forEach(user => {
      const row = [
        `"${user.name.replace(/"/g, '""')}"`,
        `"${user.email}"`,
        `"${user.phone || ''}"`,
        `"${user.role}"`,
        user.is_church_admin ? 'Oui' : 'Non',
        `"${new Date(user.joined_at).toLocaleDateString()}"`,
        `"${new Date(user.last_activity).toLocaleDateString()}"`,
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `membres_${church?.nom.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';

    switch (userSortField) {
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case 'email':
        aVal = a.email.toLowerCase();
        bVal = b.email.toLowerCase();
        break;
      case 'role':
        aVal = a.role.toLowerCase();
        bVal = b.role.toLowerCase();
        break;
      case 'last_activity':
        aVal = new Date(a.last_activity).getTime();
        bVal = new Date(b.last_activity).getTime();
        break;
    }

    if (aVal < bVal) return userSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return userSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleUserSort = (field: 'name' | 'email' | 'role' | 'last_activity') => {
    if (userSortField === field) {
      setUserSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (userSortField !== field) return null;
    return userSortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />;
  };

  // Computed dashboard stats
  const dashboardStats = React.useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - 7);
    const next7Days = new Date(now);
    next7Days.setDate(now.getDate() + 7);

    // New members this month
    const newMembersThisMonth = users.filter(u => {
      const joinedDate = new Date(u.joined_at);
      return joinedDate >= startOfMonth;
    }).length;

    // New members this week
    const newMembersThisWeek = users.filter(u => {
      const joinedDate = new Date(u.joined_at);
      return joinedDate >= startOfWeek;
    }).length;

    // Recently active members (last 7 days)
    const activeMembers = users.filter(u => {
      const lastActivity = new Date(u.last_activity);
      return lastActivity >= startOfWeek;
    }).length;

    // Upcoming events (next 7 days)
    const upcomingEvents = events.filter(e => {
      const eventDate = new Date(e.date);
      return eventDate >= now && eventDate <= next7Days;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Past events (for reference)
    const pastEvents = events.filter(e => new Date(e.date) < now).length;

    // Recent members (last 5 who joined)
    const recentMembers = [...users]
      .sort((a, b) => new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime())
      .slice(0, 5);

    return {
      newMembersThisMonth,
      newMembersThisWeek,
      activeMembers,
      upcomingEvents,
      upcomingEventsCount: upcomingEvents.length,
      pastEvents,
      recentMembers,
    };
  }, [users, events]);

  // Filtered and sorted events for Events Tab
  const filteredEvents = React.useMemo(() => {
    const now = new Date();

    let filtered = [...events];

    // Filter by tab (upcoming/past/all)
    if (eventFilterTab === 'upcoming') {
      filtered = filtered.filter(e => new Date(e.date) >= now);
    } else if (eventFilterTab === 'past') {
      filtered = filtered.filter(e => new Date(e.date) < now);
    }

    // Filter by type (recurring/one-time)
    if (eventTypeFilter === 'recurring') {
      filtered = filtered.filter(e => e.is_recurring);
    } else if (eventTypeFilter === 'oneTime') {
      filtered = filtered.filter(e => !e.is_recurring);
    }

    // Filter by search query
    if (eventSearchQuery.trim()) {
      const query = eventSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(e =>
        e.titre.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return eventSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [events, eventFilterTab, eventTypeFilter, eventSearchQuery, eventSortDirection]);

  // Event counts for tabs
  const eventCounts = React.useMemo(() => {
    const now = new Date();
    return {
      all: events.length,
      upcoming: events.filter(e => new Date(e.date) >= now).length,
      past: events.filter(e => new Date(e.date) < now).length,
    };
  }, [events]);

  // Helper function to get days until event
  const getDaysUntilEvent = (eventDate: string) => {
    const now = new Date();
    const date = new Date(eventDate);
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if event is in the past
  const isEventPast = (eventDate: string) => {
    return new Date(eventDate) < new Date();
  };

  // Chercher l'église où l'utilisateur est admin
  useEffect(() => {
    const findAdminChurch = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // D'abord vérifier dans le profil utilisateur
      let userEglises = user.eglises;

      // Si pas d'églises dans le profil, les récupérer via l'API
      if (!userEglises || userEglises.length === 0) {
        try {
          userEglises = await getUserEglises(user.id);
        } catch (err) {
          // Error handled by notification system
          setError(t('adminDashboard.errorFetching'));
          setLoading(false);
          return;
        }
      }

      // Trouver l'église où l'utilisateur est admin
      const adminChurch = userEglises?.find(e => e.is_admin);

      if (adminChurch) {
        setAdminChurchId(adminChurch.id);
        fetchChurchDetails(adminChurch.id);
      } else {
        setError(t('adminDashboard.noChurchAssigned'));
        setLoading(false);
      }
    };

    findAdminChurch();
  }, [user]);

  useEffect(() => {
    if (church) {
      if (tabValue === 1) {
        fetchUsers();
      } else if (tabValue === 2) {
        fetchEvents();
      }
    }
  }, [tabValue, church]);

  const fetchChurchDetails = async (churchId: number) => {
    try {
      setLoading(true);
      setError(null);
      const churchData = await getEgliseById(churchId);
      setChurch(churchData);
    } catch (err: any) {
      // Error handled by notification system
      setError(err.response?.data?.detail || t('adminDashboard.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  const refreshChurch = () => {
    if (adminChurchId) {
      fetchChurchDetails(adminChurchId);
    }
  };

  const fetchUsers = async () => {
    if (!church) return;
    try {
      const usersData = await getEgliseUsers(church.id);
      setUsers(usersData.users);
    } catch (err: any) {
      // Error handled by notification system
    }
  };

  const fetchEvents = async () => {
    if (!church) return;
    try {
      const eventsData = await getEgliseEvents(church.id);
      setEvents(eventsData.events);
    } catch (err: any) {
      // Error handled by notification system
    }
  };

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  const handleEventClick = useCallback((eventId: number) => {
    navigate(`/events/${eventId}`);
  }, [navigate]);

  const handleCreateEventSuccess = () => {
    setOpenEventDialog(false);
    fetchEvents();
    // Update event count
    if (church) {
      setChurch({ ...church, event_count: church.event_count + 1 });
    }
    success(t('admin.events.createSuccess'));
  };

  const handleEditChurchSuccess = () => {
    setOpenEditChurchDialog(false);
    refreshChurch();
    success(t('admin.church.updateSuccess'));
  };

  const handleEditEvent = useCallback((event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setOpenEditEventDialog(true);
  }, []);

  const handleEditEventSuccess = () => {
    setOpenEditEventDialog(false);
    setSelectedEvent(null);
    fetchEvents();
    success(t('admin.events.updateSuccess'));
  };

  const handleDeleteEventClick = useCallback((event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    setEventToDelete(event);
    setDeleteEventDialogOpen(true);
  }, []);

  const handleConfirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    try {
      setDeletingEvent(true);
      await deleteEvent(eventToDelete.id);
      setDeleteEventDialogOpen(false);
      setEventToDelete(null);
      fetchEvents();
      // Update event count
      if (church) {
        setChurch({ ...church, event_count: Math.max(0, church.event_count - 1) });
      }
      success(t('admin.events.deleteSuccess'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('admin.events.errorDeleting'));
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleRemoveUserClick = useCallback((userToRemove: EgliseUser) => {
    setUserToRemove(userToRemove);
    setRemoveUserDialogOpen(true);
  }, []);

  const handleConfirmRemoveUser = async () => {
    if (!userToRemove || !church) return;
    try {
      setRemovingUser(true);
      await removeUserFromEglise(church.id, userToRemove.id);
      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
      fetchUsers();
      // Update user count
      setChurch({ ...church, user_count: Math.max(0, church.user_count - 1) });
      success(t('admin.users.removeSuccess'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('admin.users.errorRemoving'));
    } finally {
      setRemovingUser(false);
    }
  };

  const handleNotifyEvent = useCallback(async (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notifyEventCreation(eventId);
      success(t('admin.notifications.notifySuccess'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('admin.notifications.errorCreating'));
    }
  }, [t, success, showError]);

  const handleCreateReminders = useCallback(async (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await createEventReminders(eventId);
      success(t('admin.notifications.reminderSuccess'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('admin.notifications.errorCreating'));
    }
  }, [t, success, showError]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !church) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error || t('adminDashboard.noChurchAssigned')}
        </Alert>
        <Typography variant="body1" color="text.secondary">
          {t('adminDashboard.contactSuperAdmin')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Welcome Header - Clean & Modern */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          {t('adminDashboard.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('adminDashboard.subtitle')}
        </Typography>
      </Box>

      {/* Church Card - Modern Glass Design */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px -12px rgba(37, 99, 235, 0.35)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} sx={{ position: 'relative', zIndex: 1 }}>
          {/* Church Logo/Avatar */}
          <Box
            sx={{
              width: { xs: 80, md: 90 },
              height: { xs: 80, md: 90 },
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(255,255,255,0.3)',
              flexShrink: 0,
            }}
          >
            {church.logo_url ? (
              <Avatar
                src={church.logo_url}
                alt={church.nom}
                sx={{ width: '100%', height: '100%', borderRadius: 2 }}
              />
            ) : (
              <ChurchIcon sx={{ fontSize: 40, color: 'white' }} />
            )}
          </Box>

          {/* Church Info */}
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                {church.nom}
              </Typography>
              <Tooltip title={t('admin.church.edit')}>
                <IconButton
                  onClick={() => setOpenEditChurchDialog(true)}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {church.description && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, maxWidth: 500 }}>
                {church.description}
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<PeopleIcon sx={{ color: 'white !important', fontSize: 18 }} />}
                label={`${church.user_count} ${t('admin.church.members')}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                icon={<EventIcon sx={{ color: 'white !important', fontSize: 18 }} />}
                label={`${church.event_count} ${t('admin.church.events')}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                label={church.is_active ? t('admin.church.active') : t('admin.church.inactive')}
                size="small"
                sx={{
                  bgcolor: church.is_active ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              <Chip
                icon={<AdminPanelSettingsIcon sx={{ color: '#fcd34d !important', fontSize: 18 }} />}
                label={t('admin.church.youAreAdmin')}
                size="small"
                sx={{
                  bgcolor: 'rgba(252, 211, 77, 0.25)',
                  color: '#fcd34d',
                  fontWeight: 600,
                  border: '1px solid rgba(252, 211, 77, 0.4)',
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Card>

      {/* Stats Cards - Clean Modern Design */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px -8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 24, color: '#2563eb' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {church.user_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('adminDashboard.stats.members')}
                  </Typography>
                  {dashboardStats.newMembersThisMonth > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#059669', fontWeight: 600 }}>
                      +{dashboardStats.newMembersThisMonth} {t('adminDashboard.stats.newThisMonth')}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px -8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#ede9fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EventIcon sx={{ fontSize: 24, color: '#7c3aed' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {dashboardStats.upcomingEventsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('adminDashboard.stats.upcomingEvents')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px -8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#d1fae5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 24, color: '#059669' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {dashboardStats.activeMembers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('adminDashboard.stats.activeMembers')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card
            onClick={() => setOpenEventDialog(true)}
            sx={{
              bgcolor: '#2563eb',
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                bgcolor: '#1d4ed8',
                boxShadow: '0 8px 25px -8px rgba(37, 99, 235, 0.4)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddIcon sx={{ fontSize: 24, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white' }}>
                    {t('admin.events.create')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {t('adminDashboard.stats.quickAction')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Sections - Upcoming Events & Recent Members */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Upcoming Events Preview */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {t('adminDashboard.stats.upcomingEventsTitle')}
                </Typography>
                <Chip
                  label={t('adminDashboard.stats.next7Days')}
                  size="small"
                  sx={{ bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 500 }}
                />
              </Stack>
              {dashboardStats.upcomingEvents.length > 0 ? (
                <Stack spacing={2}>
                  {dashboardStats.upcomingEvents.slice(0, 4).map((event) => (
                    <Box
                      key={event.id}
                      onClick={() => handleEventClick(event.id)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#f1f5f9' },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: '#dbeafe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <CalendarTodayIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }} noWrap>
                            {event.titre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(event.date).toLocaleDateString()} - {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EventIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('adminDashboard.stats.noUpcomingEvents')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Members Preview */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {t('adminDashboard.stats.recentMembersTitle')}
                </Typography>
                <Chip
                  label={t('adminDashboard.stats.lastJoined')}
                  size="small"
                  sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 500 }}
                />
              </Stack>
              {dashboardStats.recentMembers.length > 0 ? (
                <Stack spacing={2}>
                  {dashboardStats.recentMembers.map((member) => (
                    <Box
                      key={member.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#2563eb',
                            fontWeight: 600,
                          }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }} noWrap>
                            {member.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminDashboard.stats.joinedOn')} {new Date(member.joined_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        {member.is_church_admin && (
                          <Chip
                            icon={<AdminPanelSettingsIcon sx={{ fontSize: 14 }} />}
                            label="Admin"
                            size="small"
                            sx={{
                              bgcolor: '#2563eb',
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: 'white' },
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('adminDashboard.stats.noRecentMembers')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs - Clean Modern Design */}
      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e2e8f0',
            px: 1,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 52,
                px: 2.5,
                color: '#64748b',
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  color: '#2563eb',
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: '#2563eb',
              },
            }}
          >
            <Tab
              label={t('admin.tabs.information')}
              icon={<ChurchIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.users')}
              icon={<PeopleIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.events')}
              icon={<EventIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.enrichments')}
              icon={<AutoAwesomeIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.posts')}
              icon={<ArticleIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.notifications')}
              icon={<NotificationsIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab
              label={t('admin.tabs.feedbacks')}
              icon={<FeedbackIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('admin.church.contact')}
                    </Typography>
                    <Stack spacing={2}>
                      {church.address && (
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <LocationOnIcon color="action" />
                          <Typography variant="body2">{church.address}</Typography>
                        </Stack>
                      )}
                      {church.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon color="action" />
                          <Typography variant="body2">{church.phone}</Typography>
                        </Stack>
                      )}
                      {church.email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon color="action" />
                          <Typography variant="body2">{church.email}</Typography>
                        </Stack>
                      )}
                      {church.website && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LanguageIcon color="action" />
                          <Typography
                            variant="body2"
                            component="a"
                            href={church.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {church.website}
                          </Typography>
                        </Stack>
                      )}
                      {!church.address && !church.phone && !church.email && !church.website && (
                        <Typography variant="body2" color="text.secondary">
                          {t('adminDashboard.noContactInfo')}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('admin.church.details')}
                    </Typography>
                    <Stack spacing={2}>
                      {church.denomination && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.church.form.denomination')}
                          </Typography>
                          <Typography variant="body2">{church.denomination}</Typography>
                        </Box>
                      )}
                      {church.founded_year && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.church.form.foundedYear')}
                          </Typography>
                          <Typography variant="body2">{church.founded_year}</Typography>
                        </Box>
                      )}
                      {church.capacity && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.church.form.capacity')}
                          </Typography>
                          <Typography variant="body2">{church.capacity} {t('admin.church.people')}</Typography>
                        </Box>
                      )}
                      {church.opening_hours && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.church.form.openingHours')}
                          </Typography>
                          <Typography variant="body2">{church.opening_hours}</Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('admin.church.createdAt')}
                        </Typography>
                        <Typography variant="body2">
                          {new Date(church.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            {/* Header with action buttons */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.800' }}>
                {t('admin.tabs.users')} ({users.length})
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportUsersToCSV}
                  disabled={users.length === 0}
                  sx={{
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                  }}
                >
                  {t('common.export') || 'Export CSV'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenAddMemberDialog(true)}
                  sx={{
                    px: 3,
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  {t('admin.users.addMember')}
                </Button>
              </Stack>
            </Stack>

            <TableContainer
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                '& .MuiTableHead-root': {
                  bgcolor: 'grey.50',
                },
                '& .MuiTableRow-root:hover': {
                  bgcolor: 'grey.50',
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: 'grey.700',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      onClick={() => handleUserSort('name')}
                    >
                      <Stack direction="row" alignItems="center">
                        {t('admin.users.name')}
                        <SortIcon field="name" />
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: 'grey.700',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      onClick={() => handleUserSort('email')}
                    >
                      <Stack direction="row" alignItems="center">
                        {t('admin.users.email')}
                        <SortIcon field="email" />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'grey.700' }}>{t('admin.users.phone')}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: 'grey.700',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      onClick={() => handleUserSort('role')}
                    >
                      <Stack direction="row" alignItems="center">
                        {t('admin.users.role')}
                        <SortIcon field="role" />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'grey.700' }}>{t('admin.users.churchAdmin')}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: 'grey.700',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      onClick={() => handleUserSort('last_activity')}
                    >
                      <Stack direction="row" alignItems="center">
                        {t('admin.users.lastActivity')}
                        <SortIcon field="last_activity" />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'grey.700' }}>{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedUsers.map((userItem) => (
                    <TableRow key={userItem.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'primary.main',
                              fontWeight: 600,
                            }}
                          >
                            {userItem.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{userItem.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{userItem.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{userItem.phone || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={userItem.role}
                          size="small"
                          sx={{
                            bgcolor: 'grey.100',
                            color: 'grey.700',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {userItem.is_church_admin && (
                          <Chip
                            icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                            label="Admin"
                            size="small"
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: 'white' },
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(userItem.last_activity).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {!userItem.is_church_admin && userItem.id !== user?.id && (
                          <Tooltip title={t('admin.users.remove')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveUserClick(userItem)}
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.dark' },
                              }}
                            >
                              <PersonRemoveIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {users.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <PeopleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('admin.users.noUsers')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenAddMemberDialog(true)}
                  sx={{ mt: 2 }}
                >
                  {t('admin.users.addMember')}
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Events Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            {/* Header with action button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.800' }}>
                {t('admin.tabs.events')} ({events.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenEventDialog(true)}
                sx={{
                  px: 3,
                  bgcolor: '#2563eb',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                {t('admin.events.create')}
              </Button>
            </Stack>

            {/* Filter Tabs */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
                  label={`${t('admin.events.filters.upcoming')} (${eventCounts.upcoming})`}
                  onClick={() => setEventFilterTab('upcoming')}
                  sx={{
                    bgcolor: eventFilterTab === 'upcoming' ? '#2563eb' : '#f1f5f9',
                    color: eventFilterTab === 'upcoming' ? 'white' : '#64748b',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: eventFilterTab === 'upcoming' ? '#1d4ed8' : '#e2e8f0',
                    },
                    '& .MuiChip-icon': {
                      color: eventFilterTab === 'upcoming' ? 'white' : '#64748b',
                    },
                  }}
                />
                <Chip
                  icon={<HistoryIcon sx={{ fontSize: 18 }} />}
                  label={`${t('admin.events.filters.past')} (${eventCounts.past})`}
                  onClick={() => setEventFilterTab('past')}
                  sx={{
                    bgcolor: eventFilterTab === 'past' ? '#64748b' : '#f1f5f9',
                    color: eventFilterTab === 'past' ? 'white' : '#64748b',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: eventFilterTab === 'past' ? '#475569' : '#e2e8f0',
                    },
                    '& .MuiChip-icon': {
                      color: eventFilterTab === 'past' ? 'white' : '#64748b',
                    },
                  }}
                />
                <Chip
                  icon={<EventIcon sx={{ fontSize: 18 }} />}
                  label={`${t('admin.events.filters.all')} (${eventCounts.all})`}
                  onClick={() => setEventFilterTab('all')}
                  sx={{
                    bgcolor: eventFilterTab === 'all' ? '#7c3aed' : '#f1f5f9',
                    color: eventFilterTab === 'all' ? 'white' : '#64748b',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: eventFilterTab === 'all' ? '#6d28d9' : '#e2e8f0',
                    },
                    '& .MuiChip-icon': {
                      color: eventFilterTab === 'all' ? 'white' : '#64748b',
                    },
                  }}
                />
              </Stack>

              {/* Search and Filters Row */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('admin.events.filters.searchPlaceholder')}
                  value={eventSearchQuery}
                  onChange={(e) => setEventSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                    },
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{t('admin.events.filters.type')}</InputLabel>
                  <Select
                    value={eventTypeFilter}
                    label={t('admin.events.filters.type')}
                    onChange={(e) => setEventTypeFilter(e.target.value as 'all' | 'recurring' | 'oneTime')}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="all">{t('admin.events.filters.allTypes')}</MenuItem>
                    <MenuItem value="recurring">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RepeatIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                        <span>{t('admin.events.filters.recurring')}</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="oneTime">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EventIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                        <span>{t('admin.events.filters.oneTime')}</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title={eventSortDirection === 'asc' ? t('admin.events.filters.sortNewest') : t('admin.events.filters.sortOldest')}>
                  <IconButton
                    onClick={() => setEventSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                    sx={{
                      bgcolor: '#f1f5f9',
                      '&:hover': { bgcolor: '#e2e8f0' },
                    }}
                  >
                    {eventSortDirection === 'asc' ? (
                      <ArrowUpwardIcon sx={{ fontSize: 20, color: '#64748b' }} />
                    ) : (
                      <ArrowDownwardIcon sx={{ fontSize: 20, color: '#64748b' }} />
                    )}
                  </IconButton>
                </Tooltip>
                {(eventSearchQuery || eventTypeFilter !== 'all') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setEventSearchQuery('');
                      setEventTypeFilter('all');
                    }}
                    sx={{ color: '#64748b' }}
                  >
                    {t('admin.events.filters.clearFilters')}
                  </Button>
                )}
              </Stack>
            </Box>

            {/* Events Grid */}
            <Grid container spacing={3}>
              {filteredEvents.map((event) => {
                const isPast = isEventPast(event.date);
                const daysUntil = getDaysUntilEvent(event.date);

                return (
                <Grid item xs={12} md={6} lg={4} key={event.id}>
                  <Card
                    onClick={() => handleEventClick(event.id)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease-in-out',
                      opacity: isPast ? 0.7 : 1,
                      filter: isPast ? 'grayscale(30%)' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px -8px rgba(30, 58, 95, 0.2)',
                        opacity: 1,
                        filter: 'none',
                      },
                    }}
                  >
                    {/* Card Header with gradient */}
                    <Box
                      sx={{
                        background: isPast
                          ? 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)'
                          : event.is_recurring
                            ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                            : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                        p: 2,
                        color: 'white',
                        position: 'relative',
                      }}
                    >
                      {/* Days remaining badge for upcoming events */}
                      {!isPast && daysUntil <= 7 && (
                        <Chip
                          label={daysUntil === 0
                            ? t('admin.events.filters.today')
                            : daysUntil === 1
                              ? t('admin.events.filters.tomorrow')
                              : t('admin.events.filters.inDays', { days: daysUntil })}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: daysUntil === 0 ? '#dc2626' : daysUntil <= 3 ? '#f59e0b' : '#10b981',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                      {/* Past event badge */}
                      {isPast && (
                        <Chip
                          icon={<HistoryIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                          label={t('admin.events.filters.passed')}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.3)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flexGrow: 1, pr: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {event.titre}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {event.is_recurring && (
                              <Chip
                                icon={<RepeatIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                                label={t(`admin.events.recurrence.${event.recurrence_pattern}`)}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 24,
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: !isPast && daysUntil <= 7 ? 3 : 0,
                          }}
                        >
                          {isPast ? <HistoryIcon /> : <EventIcon />}
                        </Box>
                      </Stack>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {event.description}
                      </Typography>

                      {/* Date info */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: '#dbeafe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CalendarTodayIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.800' }}>
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        {t('admin.events.createdBy')} {event.created_by_name}
                      </Typography>
                    </CardContent>

                    {/* Action buttons */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderTop: '1px solid #f1f5f9',
                        bgcolor: '#fafafa',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 0.75,
                      }}
                    >
                      <Tooltip title={t('admin.notifications.notifyEvent')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleNotifyEvent(event.id, e)}
                          sx={{
                            bgcolor: '#dbeafe',
                            color: '#2563eb',
                            '&:hover': { bgcolor: '#bfdbfe' },
                          }}
                        >
                          <NotificationsActiveIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin.notifications.createReminders')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleCreateReminders(event.id, e)}
                          sx={{
                            bgcolor: '#fef3c7',
                            color: '#d97706',
                            '&:hover': { bgcolor: '#fde68a' },
                          }}
                        >
                          <AlarmIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin.events.edit')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditEvent(event, e)}
                          sx={{
                            bgcolor: '#e0e7ff',
                            color: '#4f46e5',
                            '&:hover': { bgcolor: '#c7d2fe' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin.events.delete')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteEventClick(event, e)}
                          sx={{
                            bgcolor: '#fee2e2',
                            color: '#dc2626',
                            '&:hover': { bgcolor: '#fecaca' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                </Grid>
              );})}
            </Grid>

            {/* No events message */}
            {events.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <EventIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('admin.events.noEvents')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenEventDialog(true)}
                  sx={{ mt: 2 }}
                >
                  {t('admin.events.create')}
                </Button>
              </Box>
            )}

            {/* No results found after filtering */}
            {events.length > 0 && filteredEvents.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <SearchIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('admin.events.filters.noResults')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('admin.events.filters.tryDifferent')}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setEventSearchQuery('');
                    setEventTypeFilter('all');
                    setEventFilterTab('all');
                  }}
                >
                  {t('admin.events.filters.clearFilters')}
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Enrichments Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <TextField
                label={t('admin.enrichments.selectDate')}
                type="number"
                size="small"
                value={selectedDay}
                onChange={(e) => setSelectedDay(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 30 }}
                sx={{ width: 100 }}
              />
              <Typography>/</Typography>
              <TextField
                label="Month"
                type="number"
                size="small"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Math.min(13, Math.max(1, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 13 }}
                sx={{ width: 100 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenEnrichmentDialog(true)}
              >
                {t('admin.enrichments.create')}
              </Button>
            </Stack>
            <EnrichmentsView key={`enrichments-${refreshKey}`} day={selectedDay} month={selectedMonth} />
          </Box>
        </TabPanel>

        {/* Posts Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <TextField
                label={t('admin.posts.selectDate')}
                type="number"
                size="small"
                value={selectedDay}
                onChange={(e) => setSelectedDay(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 30 }}
                sx={{ width: 100 }}
              />
              <Typography>/</Typography>
              <TextField
                label="Month"
                type="number"
                size="small"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Math.min(13, Math.max(1, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 13 }}
                sx={{ width: 100 }}
              />
            </Stack>
            <PostsView key={`posts-${refreshKey}`} day={selectedDay} month={selectedMonth} />
          </Box>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<NotificationsIcon />}
                onClick={() => setOpenBulkNotificationDialog(true)}
              >
                {t('admin.notifications.createBulk')}
              </Button>
            </Stack>
            <Typography variant="h6" gutterBottom>{t('admin.notifications.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin.notifications.noNotifications')}
            </Typography>
          </Box>
        </TabPanel>

        {/* Feedbacks Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ p: 2 }}>
            <FeedbacksView />
          </Box>
        </TabPanel>
      </Card>

      {/* Dialogs */}
      {church && (
        <>
          <CreateEventDialog
            open={openEventDialog}
            onClose={() => setOpenEventDialog(false)}
            egliseId={church.id}
            onSuccess={handleCreateEventSuccess}
          />

          <EditChurchDialog
            open={openEditChurchDialog}
            onClose={() => setOpenEditChurchDialog(false)}
            church={church}
            onSuccess={handleEditChurchSuccess}
          />

          <EditEventDialog
            open={openEditEventDialog}
            onClose={() => {
              setOpenEditEventDialog(false);
              setSelectedEvent(null);
            }}
            event={selectedEvent}
            onSuccess={handleEditEventSuccess}
          />

          <CreateEnrichmentDialog
            open={openEnrichmentDialog}
            onClose={() => setOpenEnrichmentDialog(false)}
            selectedDate={{ day: selectedDay, month: selectedMonth, year: selectedYear }}
            onSuccess={() => {
              setRefreshKey(prev => prev + 1);
              success(t('admin.enrichments.createSuccess'));
            }}
          />

          {selectedEventForAlarm && (
            <CreateAlarmDialog
              open={openAlarmDialog}
              onClose={() => {
                setOpenAlarmDialog(false);
                setSelectedEventForAlarm(null);
              }}
              eventId={selectedEventForAlarm}
              onSuccess={() => {
                setOpenAlarmDialog(false);
                setSelectedEventForAlarm(null);
                success(t('admin.notifications.createSuccess'));
              }}
            />
          )}

          <AddMemberDialog
            open={openAddMemberDialog}
            onClose={() => setOpenAddMemberDialog(false)}
            egliseId={church.id}
            onSuccess={() => {
              fetchUsers();
              setChurch({ ...church, user_count: church.user_count + 1 });
              success(t('admin.users.addSuccess'));
            }}
          />

          <BulkNotificationDialog
            open={openBulkNotificationDialog}
            onClose={() => setOpenBulkNotificationDialog(false)}
            egliseId={church.id}
            onSuccess={() => {
              success(t('admin.notifications.bulkSuccess'));
            }}
          />
        </>
      )}

      {/* Delete Event Confirmation Dialog */}
      <Dialog
        open={deleteEventDialogOpen}
        onClose={() => !deletingEvent && setDeleteEventDialogOpen(false)}
      >
        <DialogTitle>{t('admin.events.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.events.deleteConfirmMessage', { title: eventToDelete?.titre })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEventDialogOpen(false)} disabled={deletingEvent}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteEvent}
            color="error"
            variant="contained"
            disabled={deletingEvent}
          >
            {deletingEvent ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Confirmation Dialog */}
      <Dialog
        open={removeUserDialogOpen}
        onClose={() => !removingUser && setRemoveUserDialogOpen(false)}
      >
        <DialogTitle>{t('admin.users.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.users.removeConfirmMessage', { name: userToRemove?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveUserDialogOpen(false)} disabled={removingUser}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmRemoveUser}
            color="error"
            variant="contained"
            disabled={removingUser}
          >
            {removingUser ? t('common.loading') : t('admin.users.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(AdminDashboard);
